// Scripted gameplay news (M1: 1859-1900, plus the later price overrides from DESIGN §6.1) merged with
// the researched HISTORICAL_EVENTS headlines.
//
// SCRIPTED item: { id, day, headline, kind, regionId?, fieldId?, pause?, effect? }
//   effect: { type: 'priceOverride', price, days }   spot is pinned near `price` for `days` (market.js reads this directly)
//         | { type: 'flag', flag, value }            sim sets state.flags[flag] = value (applyNewsEffect)
//         | { type: 'boom', fieldId }                informational; lease boom is driven by FieldDef.boomCurve
//         | { type: 'localPrice', fieldIds?, regionIds?, price?, mult?, days }   local glut (market.localPrice, M2)
//   priceEffect?: x   optional short global dip/spike at the item's day, fading over 30 days (like HISTORICAL_EVENTS)
// kind: 'discovery' | 'technology' | 'market' | 'politics' | 'disaster' | 'regional'
import { dateToDay } from '../core/time.js';
import { HISTORICAL_EVENTS } from './oilPrices.js';
import { M2_SCRIPTED, M2_INCIDENT_HISTORICAL, M2_INCIDENT_TEMPLATES, M2_INCIDENT_WARNINGS } from './m2/news.js';
import { M3_SCRIPTED, M3_INCIDENT_HISTORICAL, M3_INCIDENT_TEMPLATES, M3_INCIDENT_WARNINGS } from './m3/news.js';
import { M4_SCRIPTED, M4_INCIDENT_HISTORICAL, M4_INCIDENT_TEMPLATES, M4_INCIDENT_WARNINGS } from './m4/news.js';
import { m4List, m4Obj } from './m4/gate.js';

const H = Object.fromEntries(HISTORICAL_EVENTS.map((e) => [`${e.year}-${e.month}-${e.headline.slice(0, 12)}`, e.headline]));
const hist = (year, month, start) => H[`${year}-${month}-${start}`] ?? null;

const N = (id, y, m, d, kind, headline, extra = {}) => ({ id, day: dateToDay(y, m, d), kind, headline, ...extra });

export const SCRIPTED = [
  // ------------------------------------------------------------------ 1859-1869
  N('drake_well', 1859, 8, 27, 'discovery', hist(1859, 8, 'Drake Strike') ?? 'Drake Strikes Oil at Titusville: 69 Feet Down, Rock Oil Flows', { pause: true }),
  N('drake_fire', 1859, 10, 7, 'disaster', "Drake's Derrick Burns: Lamp Ignites Gas in the Engine House"),
  N('oil_creek_rush', 1860, 3, 1, 'market', 'Lease Fever on Oil Creek: Farmers Ask Hundreds for a Rocky Acre'),
  N('empire_well', 1861, 3, 15, 'market', 'Empire Well Gushes 3,000 bbl/d; Crude Sells for a Dime, Barrels Worth More Than Oil',
    { pause: true, effect: { type: 'priceOverride', price: 0.10, days: 60 } }),
  N('civil_war', 1861, 4, 12, 'politics', hist(1861, 4, 'Civil War Er') ?? 'Civil War Erupts; Oil Glut Drives Barrel to Mere Cents'),
  N('rouseville_fire', 1861, 4, 17, 'disaster', 'Little & Merrick Well Explodes at Rouseville: Nineteen Killed in Gas Fire'),
  N('wooden_casing', 1861, 9, 1, 'technology', 'Wrought Iron Casing and Seed-Bag Packers Shut Off the Water Sands', { effect: { type: 'flag', flag: 'ironCasing', value: true } }),
  N('pithole_boom', 1865, 1, 7, 'discovery', hist(1865, 1, 'Pithole Boom') ?? 'Pithole Boom! Wildcatters Flock to Overnight Oil Town',
    { fieldId: 'pithole', pause: true, effect: { type: 'boom', fieldId: 'pithole' } }),
  N('coal_oil_johnny', 1865, 3, 1, 'market', "'Coal Oil Johnny' Steele Inherits a Fortune in Oil and Sets Out to Spend It"),
  N('rail_cars', 1865, 9, 1, 'technology', 'Densmore Tank Cars Roll: Bulk Crude Moves by Rail Without a Single Barrel',
    { effect: { type: 'flag', flag: 'railTankCars', value: true } }),
  N('van_syckel', 1865, 10, 9, 'technology', hist(1865, 10, 'Van Syckel O') ?? 'Van Syckel Opens First Oil Pipeline: Teamsters Fume',
    { effect: { type: 'flag', flag: 'pipelines', value: true } }),
  N('roberts_torpedo', 1866, 11, 20, 'technology', "Col. Roberts Patents the Exploding Torpedo; 'Moonlighters' Shoot Wells by Night",
    { effect: { type: 'flag', flag: 'torpedo', value: true } }),
  N('pithole_bust', 1866, 8, 1, 'market', 'Pithole Wells Run Dry; Fires Sweep the Boomtown as Speculators Flee', { fieldId: 'pithole' }),
  // ------------------------------------------------------------------ 1870-1879
  N('standard_oil', 1870, 1, 10, 'market', hist(1870, 1, 'Rockefeller ') ?? 'Rockefeller Incorporates Standard Oil Company of Ohio'),
  N('bradford_opens', 1871, 9, 1, 'discovery', "Deep Wells at Bradford Find a Sand 'As Wide as the County'", { fieldId: 'bradford', pause: true }),
  N('south_improvement', 1872, 2, 26, 'market', hist(1872, 2, 'South Improv') ?? 'South Improvement Scheme Exposed; Oil Region in Revolt',
    { effect: { type: 'flag', flag: 'standardOilDiscount', value: true } }),
  N('baku_auction', 1872, 12, 1, 'regional', 'Tsar Ends State Monopoly: Baku Oil Lands Go to Auction', { regionId: 'baku', fieldId: 'baku', pause: true }),
  N('baku_gusher', 1873, 6, 1, 'regional', "Khalafi Well Blows at Balakhany: Baku's First Great Gusher Runs for Months", { regionId: 'baku', fieldId: 'baku' }),
  N('panic_1873', 1873, 9, 18, 'market', 'Jay Cooke Fails: Panic of 1873 Grips Wall Street and the Oil Exchanges'),
  N('russo_turkish', 1877, 4, 24, 'regional', 'Russia Declares War on the Ottomans; Armies March Through Wallachia', { regionId: 'romania' }),
  N('nobel_pipeline', 1878, 11, 1, 'regional', "Nobel Brothers Lay Baku's First Pipeline; Carters Riot", { regionId: 'baku' }),
  N('tidewater', 1879, 5, 28, 'technology', 'Tidewater Pipeline Pumps Crude Over the Alleghenies: Independents Break the Standard Squeeze',
    { pause: true, effect: { type: 'flag', flag: 'tidewater', value: true } }),
  N('bradford_glut', 1879, 7, 1, 'market', hist(1879, null, 'Bradford Fie') ?? 'Bradford Field Floods Market; Crude Falls Below a Dollar', { fieldId: 'bradford' }),
  // ------------------------------------------------------------------ 1880-1900
  N('standard_trust', 1882, 1, 2, 'market', hist(1882, 1, 'Standard Oil') ?? 'Standard Oil Trust Formed: 90% of US Refining Under One Roof'),
  N('baku_railway', 1883, 5, 1, 'regional', 'Transcaucasian Railway Reaches Batumi: Baku Oil Rolls to the Black Sea', { regionId: 'baku' }),
  N('droozhba', 1883, 9, 1, 'regional', "Droozhba Gusher Spouts 300 Feet High at Baku, Unstoppable for Four Months", { regionId: 'baku', fieldId: 'baku', pause: true }),
  N('lima_opens', 1885, 5, 1, 'discovery', hist(1885, null, 'Lima-Indiana') ?? "Lima-Indiana Field Opens; Sour 'Skunk Oil' Floods West", { fieldId: 'lima' }),
  N('anticline_theory', 1885, 6, 1, 'technology', "I.C. White's Anticline Theory: Geologists Now Find Oil Where Creekologists Can't"),
  N('telaga_said', 1885, 6, 15, 'regional', 'Zijlker Strikes Oil at Telaga Said in the Sumatran Jungle', { regionId: 'sumatra', fieldId: 'telaga_said' }),
  N('frasch', 1888, 3, 1, 'technology', "Frasch Process Sweetens Lima Crude: 'Skunk Oil' Fetches Near Full Price",
    { fieldId: 'lima', effect: { type: 'flag', flag: 'frasch', value: true } }),
  N('baku_rivals', 1891, 6, 1, 'market', hist(1891, null, 'Baku Oil Riv') ?? 'Baku Oil Rivals America; Nobel Brothers Ship Worldwide', { regionId: 'baku' }),
  N('murex', 1892, 8, 23, 'regional', "Samuel's Tanker Murex Carries Baku Kerosene Through Suez to the Far East", { regionId: 'baku' }),
  N('seep_agency', 1895, 1, 30, 'market', 'Standard Abolishes the Oil Exchange: Seep Agency Now Posts the Price'),
  N('baku_leads', 1898, 6, 1, 'regional', 'Russia Overtakes America: Baku Now the World’s Greatest Oil Field', { regionId: 'baku' }),
  N('turn_of_century', 1900, 12, 1, 'market', 'Pennsylvania Fields in Decline; Drillers Eye Rumors From a Texas Salt Dome'),

  // ------------------------------------------------------------------ later-era scripted market overrides (DESIGN §6.1)
  N('east_texas_glut', 1931, 8, 17, 'market', hist(1931, 8, 'Martial Law ') ?? 'Martial Law in East Texas Oil Field as Crude Sells for a Dime',
    // M2_SPEC §18.1: East Texas posted a dime while the 1931 US average was $0.65, so this is a LOCAL glut (Texas and
    // Oklahoma wellheads, see market.localPrice) plus a short global dip (priceEffect, fades over 30 days), not a world override.
    { pause: true, priceEffect: 0.6, effect: { type: 'localPrice', regionIds: ['texas', 'oklahoma'], price: 0.10, days: 90 } }),
  N('prorationing', 1931, 8, 18, 'politics', 'Railroad Commission Imposes Prorationing: Wells Held to Their Allowable',
    { effect: { type: 'flag', flag: 'prorationing', value: true } }),
  N('netback_war', 1986, 3, 1, 'market', 'Netback Price War: Saudi Barrels Flood In, Crude Pinned Near $10',
    // M3_SPEC §18.4: verified WTI Mar-Jun 1986 averages ~$13.5 (low $10.25 on 31 Mar), so pin at 12.5, not 10
    { pause: true, effect: { type: 'priceOverride', price: 12.5, days: 120 } }),
  N('piper_alpha', 1988, 7, 6, 'disaster', hist(1988, 7, 'Piper Alpha ') ?? 'Piper Alpha Explodes in North Sea; 167 Dead',
    // M3_SPEC §18.5: informational flag only; the M3 sim prices offshore cover with the decaying OFFSHORE_INSURANCE table
    { effect: { type: 'flag', flag: 'piperAlpha', value: true } }),
  N('macondo', 2010, 4, 20, 'disaster', hist(2010, 4, 'Deepwater Ho') ?? 'Deepwater Horizon Explodes; 11 Dead, Gulf Spill Uncapped',
    { pause: true, effect: { type: 'flag', flag: 'bopInspection', value: true } }),
  // 2020-04-20 negative print itself is part of basePriceForDay(); the aftermath is an override.
  N('negative_oil', 2020, 4, 20, 'market', hist(2020, 4, 'Oil Goes NEG') ?? 'Oil Goes NEGATIVE: WTI Settles at Minus $37.63', { pause: true }),
  N('covid_aftermath', 2020, 4, 21, 'market', 'Storage Brimming: Buyers Pay $15 for Barrels Nobody Wants', { effect: { type: 'priceOverride', price: 15, days: 30 } }),

  // ------------------------------------------------------------------ Gusher & Rotary 1901-1945 (M2): src/data/m2/news.js
  ...M2_SCRIPTED,
  // ------------------------------------------------------------------ Postwar & Offshore 1946-1999 (M3): src/data/m3/news.js
  ...M3_SCRIPTED,
  // ------------------------------------------------------------------ Shale & Deepwater 2000-2025 (M4): src/data/m4/news.js
  ...m4List(M4_SCRIPTED),
].sort((a, b) => a.day - b.day);

// ------------------------------------------------------------------ accidents & safety (DESIGN §15)
// historical: real safety news for the ticker (kind 'disaster'); merged into NEWS.
// templates: headline/log templates for the player's own incidents, keyed by incident type.
//   Placeholders: {name} worker, {rig} rig name, {field} field name, {company} company name.
const I = (id, y, m, d, incident, headline) => ({ id, day: dateToDay(y, m, d), kind: 'disaster', incident, headline });
export const INCIDENT_FLAVOR = {
  historical: [
    I('safety_pithole_fire', 1866, 2, 1, 'well_fire', 'Fire Sweeps Pithole Wells; Derricks Burn Like Torches Along the Creek'),
    I('safety_nitro_wagon', 1870, 8, 1, 'torpedo', 'Nitroglycerin Wagon Explodes on Titusville Road; Shooter and Team Lost'),
    I('safety_boiler_1874', 1874, 3, 1, 'boiler_explosion', 'Boiler Bursts at Petroleum Centre Rig; Engine House Leveled'),
    I('safety_tank_fire_1880', 1880, 6, 11, 'tank_fire', 'Lightning Fires Titusville Tank Farm; Boilover Spreads Burning Oil Down the Valley'),
    I('safety_bradford_fire', 1880, 7, 1, 'tank_fire', 'Bradford Tanks Struck in Summer Storms; Thousands of Barrels Burn'),
    I('safety_boiler_1883', 1883, 4, 1, 'boiler_explosion', "Another Boiler Explosion in the Oil Region: 'Tied-Down Safety Valve' Blamed"),
    I('safety_cathead', 1886, 9, 1, 'machinery', 'Tool Dresser Maimed by Cathead Rope; Doctors Call the Bull Wheel the Driller\u2019s Widowmaker'),
    I('safety_lima_gas', 1887, 5, 1, 'h2s', "Lima Drillers Overcome by 'Skunk Gas' in Well Cellar; Two Men Dead"),
    I('safety_baku_fire', 1888, 8, 1, 'well_fire', 'Balakhany Gusher Ignites; Fire Burns for Weeks Over the Black Town'),
    I('safety_flood_1892', 1892, 6, 5, 'tank_fire', 'Oil Creek Flood Carries Burning Benzine Into Titusville and Oil City; Scores Dead'),
    I('safety_fall_1897', 1897, 1, 1, 'fall', 'Derrickman Falls From Icy Crown Block; Calls Grow for Ladders on Every Rig'),
    ...M2_INCIDENT_HISTORICAL,
    ...M3_INCIDENT_HISTORICAL,
    ...m4List(M4_INCIDENT_HISTORICAL),
  ],
  templates: {
    blowout: ['{rig} blows out at {field}! Gas and oil roar over the crown block.'],
    well_fire: ['Fire at {rig}! Gas reached the boiler and the derrick is ablaze.', 'Lantern ignites gas at {rig}; crew flees the floor.'],
    h2s: ['Rotten-egg stench on the floor at {rig} \u2014 then nothing. {name} collapses.', 'Sour gas knockdown at {rig}. Rescuers must hold their breath.'],
    fall: ['{name} falls from the derrick at {rig}.'],
    boiler_explosion: ['Boiler explodes at {rig}! The engine house is gone.'],
    torpedo: ['Torpedo goes off early at {rig}; {name} was lowering the shell.'],
    machinery: ['{name} caught in the bull wheel rope at {rig}.', 'Cathead rope snags {name} at {rig}.'],
    struck_by: ['Swinging tools strike {name} on the floor of {rig}.', 'Snapped line whips across the floor at {rig}; {name} hurt.'],
    tank_fire: ['Lightning strikes a tank at {field}; burning oil boils over.'],
    cellar_gas: ['Gas pooled in the well cellar at {rig}; {name} overcome.'],
    teamster: ['Wagon overturns in the mud on the {field} road; barrels and {name} in the creek.'],
    ...M2_INCIDENT_TEMPLATES,
    ...M3_INCIDENT_TEMPLATES,
    ...m4Obj(M4_INCIDENT_TEMPLATES),
  },
  warnings: {
    boiler_explosion: 'Boiler pressure gauge sticking',
    h2s: 'Rotten-egg smell on the floor',
    fall: 'Derrickman complaining of exhaustion',
    cellar_gas: 'Gas bubbling in the cellar',
    well_fire: 'Gas smell near the engine house',
    machinery: 'Frayed cathead rope',
    struck_by: 'Tools swinging loose on the line',
    tank_fire: 'Thunderheads building over the tanks',
    torpedo: 'Nitro cans sweating in the sun',
    teamster: 'Road axle-deep in mud',
    blowout: 'Gas cutting the bailer water',
    ...M2_INCIDENT_WARNINGS,
    ...M3_INCIDENT_WARNINGS,
    ...m4Obj(M4_INCIDENT_WARNINGS),
  },
};

// HISTORICAL_EVENTS converted to dated news (month null -> 1 July; day-of-month 15), excluding those
// already carried by a SCRIPTED item.
const scriptedHeads = new Set(SCRIPTED.map((s) => s.headline));
export const HISTORICAL_NEWS = HISTORICAL_EVENTS
  .filter((e) => !scriptedHeads.has(e.headline))
  .map((e, i) => ({
    id: `hist_${e.year}_${i}`, day: dateToDay(e.year, e.month ?? 7, e.month ? 15 : 1), kind: e.category,
    headline: e.headline, priceEffect: e.priceEffect,
  }));

// All news, sorted by day.
export const NEWS = [...SCRIPTED, ...HISTORICAL_NEWS, ...INCIDENT_FLAVOR.historical].sort((a, b) => a.day - b.day);

const byDay = new Map();
for (const n of NEWS) { if (!byDay.has(n.day)) byDay.set(n.day, []); byDay.get(n.day).push(n); }

// News items scheduled for exactly `day` (call once per tick).
export function newsForDay(day) {
  return byDay.get(day) ?? [];
}

// Latest value a SCRIPTED flag effect has set on or before `day` (undefined if never).
const FLAG_MEMO = new Map(); // flag -> { day, v } (pure in (flag, day); hot: proration/price checks per well per day)
export function scriptedFlag(flag, day) {
  const m = FLAG_MEMO.get(flag);
  if (m && m.day === day) return m.v;
  const v = scriptedFlagRaw(flag, day);
  FLAG_MEMO.set(flag, { day, v });
  return v;
}
function scriptedFlagRaw(flag, day) {
  let v;
  for (const s of SCRIPTED) {
    if (s.day > day) break;
    if (s.effect?.type === 'flag' && s.effect.flag === flag) v = s.effect.value;
  }
  return v;
}

// Apply a news item's effect to the game state. priceOverride/boom need no state (market.js / boomCurve).
export function applyNewsEffect(state, item) {
  const e = item.effect;
  if (!e) return;
  if (e.type === 'flag') {
    state.flags ??= {};
    state.flags[e.flag] = e.value;
  }
}
